import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { usePublicConfig } from "@/lib/publicConfig";
import { useLegalModal } from "@/components/LegalModal";

// ============================================================================
// SiteFooter — footer branded HIT TCG, usado globalmente en Layout.jsx.
// Estilos scoped al wrapper `.site-footer` para no colisionar con el resto de
// la app. Fuentes: fallback a system-ui / monospace (no depende de Archivo).
// ============================================================================

function FooterCol({ title, items, external, onClickItem }) {
  return (
    <div>
      <div className="sf-mono sf-col-h">{title}</div>
      <ul>
        {items.filter(([href]) => href).map(([href, label, testid]) => {
          const props = { "data-testid": testid };
          if (onClickItem && typeof href === "string" && href.startsWith("legal:")) {
            return (
              <li key={label}>
                <button type="button" onClick={() => onClickItem(href.slice(6))} {...props}>{label}</button>
              </li>
            );
          }
          return (
            <li key={label}>
              {external ? (
                <a href={href} target="_blank" rel="noopener" {...props}>{label}</a>
              ) : (
                <Link to={href} {...props}>{label}</Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function SiteFooter() {
  const { user } = useAuth();
  const { open: openLegal } = useLegalModal();
  const publicConfig = usePublicConfig();
  const social = publicConfig.social_links || { instagram: "", tiktok: "", whatsapp: "" };

  const isStaff = user?.role === "admin" || user?.role === "moderator";
  const marketplaceItems = [
    ["/catalog", "Catálogo", "footer-catalog"],
    ["/subastas", "Subastas", "footer-auctions"],
    ["/publish", "Publicar", "footer-publish"],
    ["/faq", "Preguntas frecuentes", "footer-faq"],
  ].filter(Boolean);
  const communityItems = [
    [social.instagram, "Instagram"],
    [social.tiktok, "TikTok"],
    [social.whatsapp, "WhatsApp"],
  ];
  const legalItems = [
    ["legal:terms", "Términos", "footer-terms"],
    ["legal:privacy", "Privacidad (LOPDP)", "footer-privacy"],
    ["legal:prohibited", "Artículos prohibidos", "footer-prohibited"],
    ["legal:image_rights", "Imágenes y PI", "footer-image-rights"],
  ];

  return (
    <footer className="site-footer" style={{ overflowAnchor: "none" }}>
      <SiteFooterStyles />
      <div className="sf-container sf-grid">
        <div>
          <div className="sf-brand">
            <span className="sf-logo">HIT<span style={{ color: "#FF4438" }}>TCG</span></span>
          </div>
          <p className="sf-desc">El primer mercado nacional de cartas Pokémon. Compra, vende y descubre el precio real hecho en Ecuador.</p>
          <div className="sf-social-row">
            {social.instagram && <a href={social.instagram} target="_blank" rel="noopener" aria-label="Instagram" className="sf-social-btn" data-testid="footer-ig">IG</a>}
            {social.tiktok && <a href={social.tiktok} target="_blank" rel="noopener" aria-label="TikTok" className="sf-social-btn" data-testid="footer-tk">TT</a>}
            {social.whatsapp && <a href={social.whatsapp} target="_blank" rel="noopener" aria-label="WhatsApp" className="sf-social-btn sf-social-wa" data-testid="footer-wa">WA</a>}
          </div>
        </div>
        <FooterCol title="Marketplace" items={marketplaceItems} />
        <FooterCol title="Comunidad" items={communityItems} external />
        <FooterCol title="Legal" items={legalItems} onClickItem={openLegal} />
      </div>
      <div className="sf-container sf-bottom">
        <span className="sf-mono">© 2026 HITTCG · ECUADOR</span>
        <span className="sf-mono" style={{ opacity: .4 }}>HECHO EN ECUADOR PARA LA ESCENA TCG</span>
      </div>
    </footer>
  );
}

function SiteFooterStyles() {
  return (
    <style>{`
      .site-footer { --sf-coral:#FF4438; --sf-green:#34D399; --sf-line:rgba(255,255,255,.09); --sf-line2:rgba(255,255,255,.14); --sf-mono:'JetBrains Mono',ui-monospace,monospace; background:#050505; border-top:1px solid var(--sf-line); padding:60px 0 30px; color:#fff; font-family:'Archivo',system-ui,sans-serif; }
      .site-footer * { box-sizing:border-box; }
      .site-footer a, .site-footer button { text-decoration:none; background:none; border:0; padding:0; cursor:pointer; font-family:inherit; text-align:left; }
      .sf-container { max-width:1720px; margin:0 auto; padding:0 clamp(16px, 4vw, 44px); }
      .sf-mono { font-family:var(--sf-mono); font-weight:700; text-transform:uppercase; letter-spacing:.14em; font-size:clamp(10px, 1.1vw, 11px); color:rgba(255,255,255,.55); }
      .sf-grid { display:grid; grid-template-columns:1fr; gap:40px; margin-bottom:40px; }
      @media (min-width:700px) { .sf-grid { grid-template-columns:2fr 1fr 1fr 1fr; gap:40px; } }
      .sf-logo { font-weight:900; font-size:28px; letter-spacing:-.02em; }
      .sf-desc { color:rgba(255,255,255,.5); font-size:14px; line-height:1.5; margin:14px 0 20px; max-width:32ch; }
      .sf-social-row { display:flex; gap:10px; }
      .sf-social-btn { display:inline-flex; align-items:center; justify-content:center; width:42px; height:42px; border-radius:50%; border:1px solid var(--sf-line2); font-family:var(--sf-mono); font-weight:700; font-size:12px; color:#fff; transition:border-color .2s, color .2s, background .2s; }
      .sf-social-btn:hover { border-color:var(--sf-coral); color:var(--sf-coral); }
      .sf-social-btn.sf-social-wa:hover { border-color:var(--sf-green); color:var(--sf-green); }
      .sf-col-h { color:rgba(255,255,255,.4); margin-bottom:16px; }
      .site-footer ul { list-style:none; padding:0; margin:0; display:grid; gap:10px; }
      .site-footer ul a, .site-footer ul button { color:rgba(255,255,255,.7); font-size:14px; transition:color .2s; display:inline-block; }
      .site-footer ul a:hover, .site-footer ul button:hover { color:#fff; }
      .sf-bottom { display:flex; flex-wrap:wrap; gap:12px; justify-content:space-between; padding-top:24px; border-top:1px solid var(--sf-line); }
    `}</style>
  );
}
